// AB#2045 — SuperAdmin "Grant founding access" modal. Provisions a
// founding/free (complimentary, non-Stripe) Trial subscription for one of the
// owner's businesses so it can add more than one branch. Calls
// POST /api/super-admin/businesses/{businessId}/grant-subscription via
// useAdminGrantSubscription. SuperAdmin-only — reachable solely from the
// SuperAdmin-guarded Owners page; the backend re-authorizes regardless.
//
// Plan-tier brand names (Starter/Growth/Pro/Enterprise) stay in Latin script
// per the plan brand-name convention and are sent as enum NAMES because the API
// serializes enums as strings (JsonStringEnumConverter).

import React, { useEffect, useState } from 'react';
import Button from '../../../components/Button/Button';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { useAdminGrantSubscription } from '../../../hooks/useAdminGrantSubscription';
import type { AdminOwnerDto, SubscriptionPlanTier } from '../../../services/api';
import { getApiErrorMessage } from '../../../types/api-errors';
import styles from './GrantSubscriptionModal.module.css';

interface GrantSubscriptionModalProps {
    open: boolean;
    onClose: () => void;
    owner: AdminOwnerDto;
    /** Called after a successful grant so the page can refresh. */
    onGranted: () => void;
}

const PLAN_TIERS: SubscriptionPlanTier[] = ['Starter', 'Growth', 'Pro', 'Enterprise'];
const DEFAULT_TIER: SubscriptionPlanTier = 'Enterprise';
const DEFAULT_COMP_DAYS = 365;

const GrantSubscriptionModal: React.FC<GrantSubscriptionModalProps> = ({
    open,
    onClose,
    owner,
    onGranted,
}) => {
    const { t } = useLanguage();
    const { showToast } = useToast();
    const grant = useAdminGrantSubscription();

    const businesses = owner.businesses ?? [];
    const [businessId, setBusinessId] = useState<number | null>(
        businesses[0]?.businessId ?? null,
    );
    const [planTier, setPlanTier] = useState<SubscriptionPlanTier>(DEFAULT_TIER);
    const [compDays, setCompDays] = useState<number>(DEFAULT_COMP_DAYS);
    const [reason, setReason] = useState<string>('');

    // Reset the form each time the modal (re)opens for an owner.
    useEffect(() => {
        if (!open) return;
        setBusinessId(businesses[0]?.businessId ?? null);
        setPlanTier(DEFAULT_TIER);
        setCompDays(DEFAULT_COMP_DAYS);
        setReason('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, owner.id]);

    if (!open) return null;

    const handleClose = () => {
        if (grant.isPending) return;
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (businessId == null) return;
        try {
            const res = await grant.mutateAsync({
                businessId,
                payload: {
                    planTier,
                    compDays,
                    reason: reason.trim() || undefined,
                },
            });
            showToast(
                res.alreadyGranted
                    ? t('superadmin.owners.grant.toast.already')
                    : t('superadmin.owners.grant.toast.granted'),
                'success',
            );
            onGranted();
            onClose();
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            const msg =
                status === 409
                    ? t('superadmin.owners.grant.error.conflict')
                    : getApiErrorMessage(err, t('superadmin.owners.grant.toast.error'));
            showToast(msg, 'error');
        }
    };

    const hasBusiness = businesses.length > 0;

    return (
        <div className={styles.modalOverlay} role="presentation" onClick={handleClose}>
            <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="grant-subscription-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h2 id="grant-subscription-title" className={styles.modalTitle}>
                        {t('superadmin.owners.grant.title')}
                    </h2>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={handleClose}
                        aria-label={t('superadmin.owners.grant.action.cancel')}
                    >
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    <div className={styles.modalContent}>
                        <p className={styles.ownerLine}>
                            {t('superadmin.owners.grant.forOwner')}{' '}
                            <span className={styles.ownerName}>{owner.fullName}</span>
                            {owner.email ? ` (${owner.email})` : ''}
                        </p>

                        {!hasBusiness && (
                            <p className={styles.notice}>
                                {t('superadmin.owners.grant.noBusiness')}
                            </p>
                        )}

                        {hasBusiness && (
                            <>
                                {businesses.length > 1 && (
                                    <div className={styles.field}>
                                        <label htmlFor="grant-business" className={styles.label}>
                                            {t('superadmin.owners.grant.business')}
                                        </label>
                                        <select
                                            id="grant-business"
                                            className={styles.select}
                                            value={businessId ?? ''}
                                            onChange={(e) => setBusinessId(Number(e.target.value))}
                                        >
                                            {businesses.map((b) => (
                                                <option key={b.businessId} value={b.businessId}>
                                                    {b.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className={styles.field}>
                                    <label htmlFor="grant-plan" className={styles.label}>
                                        {t('superadmin.owners.grant.planTier')}
                                    </label>
                                    <select
                                        id="grant-plan"
                                        className={styles.select}
                                        value={planTier}
                                        onChange={(e) =>
                                            setPlanTier(e.target.value as SubscriptionPlanTier)
                                        }
                                    >
                                        {PLAN_TIERS.map((tier) => (
                                            <option key={tier} value={tier}>
                                                {tier}
                                            </option>
                                        ))}
                                    </select>
                                    <p className={styles.hint}>
                                        {t('superadmin.owners.grant.planTier.hint')}
                                    </p>
                                </div>

                                <div className={styles.field}>
                                    <label htmlFor="grant-days" className={styles.label}>
                                        {t('superadmin.owners.grant.compDays')}
                                    </label>
                                    <input
                                        id="grant-days"
                                        type="number"
                                        min={1}
                                        max={3650}
                                        className={styles.input}
                                        value={compDays}
                                        onChange={(e) => setCompDays(Number(e.target.value))}
                                    />
                                    <p className={styles.hint}>
                                        {t('superadmin.owners.grant.compDays.hint')}
                                    </p>
                                </div>

                                <div className={styles.field}>
                                    <label htmlFor="grant-reason" className={styles.label}>
                                        {t('superadmin.owners.grant.reason')}
                                    </label>
                                    <textarea
                                        id="grant-reason"
                                        className={styles.textarea}
                                        maxLength={500}
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder={t('superadmin.owners.grant.reason.placeholder')}
                                    />
                                </div>

                                <p className={styles.notice}>
                                    {t('superadmin.owners.grant.compNotice')}
                                </p>
                            </>
                        )}
                    </div>

                    <div className={styles.modalActions}>
                        <Button
                            label={t('superadmin.owners.grant.action.cancel')}
                            variant="secondary"
                            onClick={handleClose}
                            disabled={grant.isPending}
                            type="button"
                        />
                        <Button
                            label={t('superadmin.owners.grant.action.submit')}
                            disabled={grant.isPending || !hasBusiness || businessId == null}
                            type="submit"
                        />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GrantSubscriptionModal;
